import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { DynamicForm } from '@shared/components/DynamicForm';
import { FormField } from '@shared/services/FormServices';
import { Info, Upload, FileText, Image, File, X } from 'lucide-react';
import { StepHelpers } from '@shared/components/Stepper';
import { FIELD_CLASSES } from '@shared/utils/constants';
import { SelectedAccountsBanner } from '../../../../../shared/components/SelectedAccountsBanner';
import { toast } from '@shared/hooks/use-toast';
import { Button } from '@shared/ui/button';
import { useTranslation } from 'react-i18next';

interface FileData {
  id: string;
  file?: any; 
  name: string;
  size: number;
  type: string;
  needsReupload?: boolean;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export const EvidenceStep = ({
 stepHelpers,
 currentStepIndex = 2,
 onNext,
 onPrevious
}: {
 stepHelpers?: StepHelpers;
 currentStepIndex?: number;
 onNext?: () => void;
 onPrevious?: () => void;
}) => {
 const { t } = useTranslation();
 const [selectedFiles, setSelectedFiles] = useState<FileData[]>([]);
 const fileInputRef = useRef<HTMLInputElement>(null);
 const dataLoadedRef = useRef(false);

 const getStepData = useCallback(() => {
   return stepHelpers?.getStepData(currentStepIndex) || {};
 }, [stepHelpers, currentStepIndex]);

 const form = useForm({
  defaultValues: getStepData()
 });

 useEffect(() => {
  if (stepHelpers && !dataLoadedRef.current) {
    const savedData = getStepData();
    if (savedData.evidenceFiles && Array.isArray(savedData.evidenceFiles)) {
      const windowFiles = (window as any).fileData?.fileObjects || [];
      const restoredFiles: FileData[] = savedData.evidenceFiles.map((fileMetadata: any) => {
        const actualFile = windowFiles.find(
          (f: any) => f.name === fileMetadata.name && f.size === fileMetadata.size
        );
        return {
          ...fileMetadata,
          needsReupload: !actualFile,
          file: actualFile
        };
      });
      setSelectedFiles(restoredFiles);
    }
    dataLoadedRef.current = true;
  }
 }, [stepHelpers, currentStepIndex, getStepData]);

 const saveStepData = useCallback((files: FileData[]) => {
  if (!stepHelpers) return;
  const currentData = form.getValues();
  const stepData = {
    ...currentData,
    evidenceFiles: files.map(f => ({
      id: f.id,
      name: f.name,
      size: f.size,
      type: f.type,
      needsReupload: f.needsReupload
    }))
  };
  stepHelpers.setStepData(currentStepIndex, stepData);
  
  (window as any).fileData = {
    fileObjects: files.filter(f => !f.needsReupload && f.file).map(f => f.file)
  };
 }, [stepHelpers, currentStepIndex, form]);

 useEffect(() => {
  const subscription = form.watch(() => {
   saveStepData(selectedFiles);
  });
  return () => subscription.unsubscribe();
 }, [form, selectedFiles, saveStepData]);

 const detailsFields: FormField[] = [
  {
   name: 'incidentDate',
   label: t("Incident Date"),
   type: 'datepicker',
   required: true,
   placeholder: 'dd/mm/yyyy',
   helperText: t("Select the date of the incident"),
   tooltipText: t("Date when the issue occurred")
  },
  {
   name: 'expectedResolution',
   label: t("Expected Resolution"),
   type: 'textarea',
   placeholder: t("Describe the resolution you expect..."),
   helperText: t("Provide details about the expected outcome"),
   tooltipText: t("What outcome would resolve this complaint?")
  }
 ];

 const notesFields: FormField[] = [
  {
   name: 'notes',
   label: t("Additional Notes"),
   type: 'textarea',
   placeholder: t("Add any additional notes about the evidence or attachments"),
   helperText: t("Provide additional context or details about the evidence"),
   tooltipText: t("Any other relevant information")
  }
 ];

 const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles: FileData[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`File "${file.name}" ${t("exceeds 10MB limit")}`);
        return;
      }
      const isDuplicate = selectedFiles.some((f) => f.name === file.name && f.size === file.size);
      if (isDuplicate) {
        errors.push(`File "${file.name}" ${t("is already selected")}`);
        return;
      }
      newFiles.push({
        id: `${file.name}-${Date.now()}`,
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        needsReupload: false,
      });
    });

    if (errors.length > 0) {
      toast({ title: t("File Upload Errors"), description: errors.join(", "), variant: "destructive" });
    }

    if (newFiles.length > 0) {
      const updatedFiles = [...selectedFiles, ...newFiles];
      setSelectedFiles(updatedFiles);
      saveStepData(updatedFiles);
    }
    if (event.target) event.target.value = "";
 };

 const removeFile = (fileId: string) => {
    const updatedFiles = selectedFiles.filter((f) => f.id !== fileId);
    setSelectedFiles(updatedFiles);
    saveStepData(updatedFiles);
 };

 const getFileIcon = (type: string) => {
  if (type === "application/pdf") return <FileText className="h-6 w-6 text-red-500" />;
  if (type.startsWith("image/")) return <Image className="h-6 w-6 text-blue-500" />;
  return <File className="h-6 w-6 text-gray-500" />;
 };

 return (
 <div className="space-y-4">
  <SelectedAccountsBanner stepHelpers={stepHelpers} />

  <div className="bg-white border border-gray-200 rounded-xl p-6">
   <DynamicForm form={form} fields={detailsFields} gridColumns={1} config={{classes:FIELD_CLASSES ,showSubmitButton: false, showResetButton: false}} />
  </div>

  <div className="bg-white border border-gray-200 rounded-xl p-6">
    <div className="space-y-4">
      <div
        className="border-2 border-dashed rounded-lg p-6 flex flex-col sm:flex-row items-center gap-6 hover:bg-blue-50/50 transition-colors cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="p-4 rounded-full shrink-0 bg-gray-50 group-hover:bg-blue-100 transition-colors">
          <Upload className="h-8 w-8 text-gray-400 group-hover:text-blue-500" />
        </div>
        <div className="flex flex-col gap-2 flex-grow">
          <h3 className="font-medium text-gray-900">{t("Upload Files")}</h3>
          <p className="text-sm text-gray-600">{t("Drag & drop files here or click to browse")}</p>
          <div className="flex items-center gap-3">
            <Button type="button" variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
              <Upload className="mr-2 h-4 w-4" /> {t("Choose Files")}
            </Button>
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <FileText className="h-3 w-3" /> {t("Max size: 10MB")}
            </p>
          </div>
        </div>
        <input ref={fileInputRef} type="file" className="hidden" multiple accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileUpload} />
      </div>

      {selectedFiles.length > 0 && (
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm mt-4">
          <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <File className="h-4 w-4 text-blue-500" /> {t("Selected Files")} ({selectedFiles.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {selectedFiles.map((fileData) => (
              <div key={fileData.id} className={`flex items-center gap-3 p-3 border rounded-lg transition-all ${fileData.needsReupload ? "border-orange-200 bg-orange-50/50" : "border-gray-100 bg-gray-50/50 hover:border-blue-200 hover:bg-blue-50/30"}`}>
                <div className="flex-shrink-0">{getFileIcon(fileData.type)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 truncate">{fileData.name}</p>
                    {fileData.needsReupload && (
                      <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-medium">{t("Restore Required")}</span>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-500">{formatFileSize(fileData.size)}</p>
                </div>
                <button type="button" onClick={(e) => { e.stopPropagation(); removeFile(fileData.id); }} className="flex-shrink-0 p-1.5 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-md transition-colors">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>

  <div className="bg-white border border-gray-200 rounded-xl p-6">
   <DynamicForm form={form} fields={notesFields} gridColumns={1} config={{classes:FIELD_CLASSES ,showSubmitButton: false, showResetButton: false}} />
  </div>
  
  <div className="flex justify-between pt-6 border-t border-gray-200">
  <button
   onClick={onPrevious}
   className="px-6 py-2.5 rounded-lg text-sm font-medium transition-colors bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
  >
   {t("Previous")}
  </button>
  <button
   onClick={() => {
    // Force form validation
    const values = form.getValues();
    if (!values.incidentDate) {
      toast({ title: t("Validation Error"), description: t("Incident Date is required"), variant: "destructive" });
      return;
    }
    if (onNext) onNext();
   }}
   className="px-6 py-2.5 rounded-lg text-sm font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700"
  >
   {t("Continue")}
  </button>
  </div>
 </div>
 );
};
